// POST /chat/{profile} NDJSON 스트림을 StreamEvent의 AsyncGenerator로 읽는다.
//
// 서버는 한 줄(\n 구분)에 하나의 JSON 이벤트를 내보낸다(APPLICATION.md §2.3).
// 프레이밍(버퍼링·라인 분할)은 공용 `readDelimitedStream`에 위임하고 여기선 이벤트 파싱만 한다.

import { apiRequest } from '~/api/client'
import type { ChatProfile, ChatRequest, StreamEvent } from '~/api/types'
import { readDelimitedStream } from '~/lib/stream'

const EVENT_TYPES = new Set([
    'tool_call',
    'tool_result',
    'ai_message',
    'citation',
    'done',
    'error',
    'suggestions',
])

function parseLine(line: string): StreamEvent | null {
    const trimmed = line.trim()
    if (!trimmed) return null
    try {
        const parsed = JSON.parse(trimmed) as { type?: string }
        if (typeof parsed.type === 'string' && EVENT_TYPES.has(parsed.type)) {
            return parsed as StreamEvent
        }
        return null
    } catch {
        // 깨진 라인은 무시한다(부분 청크가 아닌 진짜 깨진 경우).
        return null
    }
}

/**
 * 채팅 스트림을 시작하고 이벤트를 순차로 yield한다.
 *
 * @param profile  certification | consultation
 * @param body     사용자 메시지 + 세션/맥락
 * @param signal   취소용 AbortSignal(선택)
 */
export async function* startChatStream(
    profile: ChatProfile,
    body: ChatRequest,
    signal?: AbortSignal
): AsyncGenerator<StreamEvent, void, void> {
    const res = await apiRequest(`/chat/${profile}`, {
        method: 'POST',
        body,
        headers: { Accept: 'application/x-ndjson' },
        signal,
    })

    if (!res.ok || !res.body) {
        let message = `채팅 스트림 시작에 실패했어요 (status ${res.status})`
        try {
            const data = (await res.json()) as { message?: string }
            if (data.message) message = data.message
        } catch {
            // 본문 없음/비JSON
        }
        yield { type: 'error', code: 'StreamStartError', message }
        return
    }

    for await (const line of readDelimitedStream(res.body)) {
        const event = parseLine(line)
        if (event) yield event
    }
}
